import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../hooks/useSEO";
import { useReveal } from "../hooks/useReveal";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Press() {
  useSEO(
    "Presse",
    "Communiqués officiels et dossier de presse du Stand d'Or, première cérémonie des awards de l'humour francophone.",
  );

  const [items, setItems] = useState([]);
  const listRef = useReveal();
  const dossierRef = useReveal();

  useEffect(() => {
    api.get("/press").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="press-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden grain"
        style={{ background: "#000", minHeight: "55vh", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
      >
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 60%, #000 100%)" }}
          aria-hidden="true"
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pb-20 pt-[180px] w-full">
          <p className="label manifeste-fade" style={{ color: "#C9A961", marginBottom: 28 }}>
            Le Stand d'Or — Salle de presse
          </p>
          <h1
            className="font-serif manifeste-title"
            style={{
              fontSize: "clamp(64px, 12vw, 180px)",
              lineHeight: 0.9,
              letterSpacing: "-0.035em",
              color: "#F8F6F0",
            }}
            data-testid="press-title"
          >
            Presse
          </h1>
        </div>
      </section>

      {/* DOSSIER */}
      <section
        ref={dossierRef}
        className="reveal"
        style={{ borderTop: "1px solid #1c1c1c", borderBottom: "1px solid #1c1c1c", background: "#050505" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <p className="label mb-4" style={{ color: "#A1A1AA" }}>Dossier de presse</p>
            <p style={{ color: "rgba(248,246,240,0.75)", fontSize: 16, lineHeight: 1.75, maxWidth: 560 }}>
              Le dossier de presse complet — présentation, biographies du jury, visuels haute définition, charte
              graphique et plan de cérémonie — est disponible sur demande à l'adresse{" "}
              <a
                href="mailto:presse@lestanddor.com"
                style={{ color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.5)", paddingBottom: 1 }}
              >
                presse@lestanddor.com
              </a>
              .
            </p>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex md:justify-end">
            <Link
              to="/presse/kit"
              className="label"
              style={{ color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.4)", paddingBottom: 3 }}
            >
              Palette & identité visuelle →
            </Link>
          </div>
        </div>
      </section>

      {/* COMMUNIQUÉS */}
      <section
        ref={listRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-40"
        data-testid="press-list"
      >
        <p className="label mb-16" style={{ color: "#C9A961" }}>Communiqués officiels</p>

        {items.length === 0 ? (
          <p style={{ color: "rgba(248,246,240,0.4)", fontSize: 17 }}>
            Aucun communiqué publié pour le moment.
          </p>
        ) : (
          <ul style={{ borderTop: "1px solid #1c1c1c" }}>
            {items.map((p, i) => (
              <li key={p.id} style={{ borderBottom: "1px solid #1c1c1c" }}>
                <Link
                  to={`/presse/${p.id}`}
                  className="grid grid-cols-12 gap-6 py-10 md:py-14 group"
                  style={{ display: "grid", textDecoration: "none" }}
                  data-testid={`press-item-${p.id}`}
                >
                  <span
                    className="col-span-2 md:col-span-1 label pt-3"
                    style={{ color: "#A1A1AA" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="col-span-10 md:col-span-3 label pt-3"
                    style={{ color: "#A1A1AA" }}
                  >
                    {formatDate(p.date)}
                  </span>
                  <span className="col-span-12 md:col-span-8">
                    <span
                      className="font-serif block"
                      style={{
                        fontSize: "clamp(26px, 3vw, 48px)",
                        lineHeight: 1.08,
                        color: "#F8F6F0",
                        transition: "color 0.4s ease",
                        letterSpacing: "-0.015em",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#C9A961")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "#F8F6F0")}
                    >
                      {p.title}
                    </span>
                    {p.excerpt && (
                      <span
                        className="block mt-3"
                        style={{ color: "rgba(248,246,240,0.55)", fontSize: 15, lineHeight: 1.65 }}
                      >
                        {p.excerpt}
                      </span>
                    )}
                    <span
                      className="label inline-block mt-5"
                      style={{ color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.4)", paddingBottom: 2 }}
                    >
                      Lire le communiqué →
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
